import {
  Agent,
  CheckoutSession,
  getCheckoutSessionById,
  LoanStatusExternal,
  OnboardingFulfilledBy,
  PaymentMethodType,
} from '@nemuru-eng/js-api-client';

import { CheckoutSessionEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/_shared/entities/CheckoutSessionEntity';
import { PaymentMethodDetailEntity } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/entities/PaymentMethodDetail/PaymentMethodDetailEntity';
import { PaymentMethodDetailFactory } from '@/components/modules/CheckoutSession/Detail/CheckoutSessionDetail/PaymentMethods/PaymentMethod/_shared/entities/PaymentMethodDetail/PaymentMethodDetailFactory';

export type Customer = NonNullable<CheckoutSession['customer']>;

export class CheckoutSessionDetailEntity extends CheckoutSessionEntity {
  private static readonly MAX_POLLING_ATTEMPTS = 15; // 2 minutes
  private static readonly POLLING_INTERVAL_MS = 2000; // 3 seconds

  static readonly PAYMENT_METHOD_TYPES_WITH_PRICINGS = [
    PaymentMethodType.AUTO_LOAN,
    PaymentMethodType.BNPL_INSTALMENTS,
    PaymentMethodType.BNPL_SPLIT_PAY,
    PaymentMethodType.CREDIT_LINE,
    PaymentMethodType.CONSUMER_LOAN_INSTALMENTS,
    PaymentMethodType.CONSUMER_LOAN_SPLIT_PAY,
    PaymentMethodType.B2B_LOAN,
    PaymentMethodType.SUBSCRIPTION,
  ];

  static readonly PAYMENT_METHOD_TYPES_WITH_CART = [
    PaymentMethodType.AUTO_LOAN,
    PaymentMethodType.BNPL_INSTALMENTS,
    PaymentMethodType.BNPL_SPLIT_PAY,
    PaymentMethodType.CREDIT_LINE,
    PaymentMethodType.CONSUMER_LOAN_INSTALMENTS,
    PaymentMethodType.CONSUMER_LOAN_SPLIT_PAY,
    PaymentMethodType.B2B_LOAN,
    PaymentMethodType.SUBSCRIPTION,
    PaymentMethodType.CARD_PAYMENT,
    PaymentMethodType.A2A_PAYMENT,
  ];

  private readonly _agent: Agent;

  constructor(value: CheckoutSession, agent: Agent) {
    super(value);
    this._agent = agent;
  }

  public get agent(): Agent {
    return this._agent;
  }

  public isSelectedPaymentMethod(paymentMethodId: string): boolean {
    if (paymentMethodId === this.selectedPaymentMethodId) {
      return true;
    }
    return false;
  }

  public isShareable(): boolean {
    if (!this._checkoutSession.hpp) return false;
    if (this.hasFinalStatus()) return false;
    if (this.isAgentAllowedToShare() === false) return false;
    return true;
  }

  public isAgentAllowedToShare(): boolean {
    return this._agent.allowedOnboardingFulfilledByTypes.some((type) =>
      [
        OnboardingFulfilledBy.BORROWER_ONLINE,
        OnboardingFulfilledBy.BORROWER_POS,
        OnboardingFulfilledBy.PAY_BY_LINK,
      ].includes(type),
    );
  }

  public isAllowedToModify(paymentMethodDetailEntities: PaymentMethodDetailEntity[] | undefined): boolean {
    const paymentMethodsAllowedToModify = paymentMethodDetailEntities?.filter((pmde) => pmde.isAllowedToModify());

    if (paymentMethodsAllowedToModify && paymentMethodsAllowedToModify.length > 1) {
      return false;
    }

    return true;
  }

  public async createPaymentMethodDetailEntities(): Promise<PaymentMethodDetailEntity[]> {
    const paymentMethodTypes = this._checkoutSession.paymentMethods.map((pm) => pm.type);

    const paymentMethodDetailEntities = paymentMethodTypes.map((paymentMethodType) => {
      const paymentIntent = this.getPaymentIntent(paymentMethodType) || undefined;

      const paymentMethodDetailEntity = PaymentMethodDetailFactory.create(paymentMethodType);

      return new paymentMethodDetailEntity(paymentMethodType, this._checkoutSession, this._agent, paymentIntent);
    });

    // Only refresh the selected payment method
    if (this.selectedPaymentMethodType === null) {
      return await this.refreshPaymentMethodDetailEntities(paymentMethodDetailEntities);
    }

    const refreshedEntities: PaymentMethodDetailEntity[] = [];
    for (const paymentMethodDetailEntity of paymentMethodDetailEntities) {
      if (paymentMethodDetailEntity.type === this.selectedPaymentMethodType) {
        refreshedEntities.push(await paymentMethodDetailEntity.refresh());
      } else {
        refreshedEntities.push(paymentMethodDetailEntity);
      }
    }

    return refreshedEntities;
  }

  public async waitUntilPaymentIntentStatus(paymentMethodId: string, status: LoanStatusExternal): Promise<this> {
    const checkoutSession = await this.getCheckoutSessionIfPaymentIntentStatus(paymentMethodId, status);
    this._checkoutSession = checkoutSession;
    await this.createPaymentMethodDetailEntities();
    return this;
  }

  private async getCheckoutSessionIfPaymentIntentStatus(
    paymentMethodId: string,
    status: LoanStatusExternal,
  ): Promise<CheckoutSession> {
    const checkoutSessionId = this._checkoutSession.id;
    let attempts = 0;
    return new Promise((resolve, reject) => {
      (async function waitForPaymentIntentStatus() {
        attempts += 1;
        if (attempts > CheckoutSessionDetailEntity.MAX_POLLING_ATTEMPTS) {
          return reject();
        }

        const checkoutSession = await getCheckoutSessionById({ checkoutSessionId });

        const paymentIntent = checkoutSession.paymentIntents.find((pi) => pi.paymentMethodId === paymentMethodId);

        if (!paymentIntent) {
          return reject();
        }

        if (paymentIntent.status === status) {
          return resolve(checkoutSession);
        }

        setTimeout(waitForPaymentIntentStatus, CheckoutSessionDetailEntity.POLLING_INTERVAL_MS);
      })();
    });
  }

  public async refreshPaymentMethodDetailEntities(
    paymentMethodDetailEntities: PaymentMethodDetailEntity[],
  ): Promise<PaymentMethodDetailEntity[]> {
    return await Promise.all(
      paymentMethodDetailEntities.map(async (paymentMethodDetailEntity) => await paymentMethodDetailEntity.refresh()),
    );
  }

  public async refresh(): Promise<this> {
    const checkoutSession = await getCheckoutSessionById({ checkoutSessionId: this._checkoutSession.id });
    this._checkoutSession = checkoutSession;
    return this;
  }

  public requiresFetchingPricings(): boolean {
    return this._checkoutSession.paymentMethods.some((pm) =>
      CheckoutSessionDetailEntity.PAYMENT_METHOD_TYPES_WITH_PRICINGS.includes(pm.type),
    );
  }

  public hasCart(): boolean {
    return this._checkoutSession.paymentMethods.some((pm) =>
      CheckoutSessionDetailEntity.PAYMENT_METHOD_TYPES_WITH_CART.includes(pm.type),
    );
  }
}
